/**
 * Investor Encompass Template Service
 *
 * Service layer for managing synced Encompass Investor Template data.
 * Handles fetching, syncing, and linking templates to investors.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type {
  InvestorEncompassTemplate,
  InvestorEncompassTemplateRow,
  InvestorEncompassTemplateContactRow,
  InvestorTemplateProductAssignment,
  InvestorTemplateProductAssignmentRow,
  EncompassTemplateContact,
  SyncInvestorTemplatesRequest,
  SyncInvestorTemplatesResponse
} from '@/types/investor-encompass';

const log = createComponentLogger('InvestorEncompassTemplateService', 'database');

let productAssignmentTableAvailable: boolean | null = null;

function isMissingProductAssignmentTable(error: unknown): boolean {
  const err = error as { code?: string; message?: string; details?: string };
  const text = `${err.code ?? ''} ${err.message ?? ''} ${err.details ?? ''}`.toLowerCase();
  return (
    text.includes('investor_encompass_template_product_assignments') &&
    (text.includes('schema cache') || text.includes('could not find the table') || err.code === 'PGRST205')
  );
}

async function fetchProductAssignments(
  templateIds: string[]
): Promise<InvestorTemplateProductAssignmentRow[]> {
  if (templateIds.length === 0 || productAssignmentTableAvailable === false) {
    return [];
  }

  const { data, error } = await supabase
    .from('investor_encompass_template_product_assignments')
    .select('*')
    .in('template_id', templateIds);

  if (error) {
    if (isMissingProductAssignmentTable(error)) {
      productAssignmentTableAvailable = false;
      log.warn(
        'Template product assignment table is unavailable; continuing without product scopes',
        { templateIds },
        error
      );
      return [];
    }
    log.error('Failed to fetch template product assignments', { templateIds }, error);
    return [];
  }

  productAssignmentTableAvailable = true;
  return (data || []) as InvestorTemplateProductAssignmentRow[];
}

/**
 * Transform database row to camelCase interface
 */
function transformTemplate(
  row: InvestorEncompassTemplateRow,
  contacts?: InvestorEncompassTemplateContactRow[],
  assignments?: InvestorTemplateProductAssignmentRow[]
): InvestorEncompassTemplate {
  const transformedAssignments = assignments?.map(transformAssignment) ?? [];
  return {
    id: row.id,
    organizationId: row.organization_id,
    investorId: row.investor_id,
    encompassTemplateId: row.encompass_template_id,
    templateName: row.template_name,
    templatePath: row.template_path,
    bulkSale: row.bulk_sale,
    deliveryTimeFrame: row.delivery_time_frame,
    purchaserType: row.purchaser_type,
    purchaserTypeValue: row.purchaser_type_value,
    investorCode: row.investor_code,
    encompassInvestorName: row.encompass_investor_name,
    encompassInvestorId: row.encompass_investor_id,
    servicingTransferDate: row.servicing_transfer_date,
    commitmentNumber: row.commitment_number,
    commitmentDate: row.commitment_date,
    commitmentExpirationDate: row.commitment_expiration_date,
    targetDeliveryDate: row.target_delivery_date,
    lockPeriodDays: row.lock_period_days,
    isPublic: row.is_public,
    contacts: contacts?.map(transformContact),
    assignments: transformedAssignments,
    applicableProducts: transformedAssignments.map((assignment) => assignment.product),
    lastSyncedAt: row.last_synced_at,
    syncStatus: row.sync_status as 'synced' | 'pending' | 'error',
    syncError: row.sync_error,
    rawTemplateData: row.raw_template_data,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

function transformAssignment(row: InvestorTemplateProductAssignmentRow): InvestorTemplateProductAssignment {
  return {
    id: row.id,
    organizationId: row.organization_id,
    investorId: row.investor_id,
    templateId: row.template_id,
    product: row.product,
    source: row.source,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

/**
 * Transform contact database row to camelCase interface
 */
function transformContact(row: InvestorEncompassTemplateContactRow): EncompassTemplateContact {
  return {
    id: row.id,
    templateId: row.template_id,
    organizationId: row.organization_id,
    contactType: row.contact_type,
    investorName: row.investor_name,
    contactName: row.contact_name,
    department: row.department,
    attentionTo: row.attention_to,
    addressStreet1: row.address_street1,
    addressStreet2: row.address_street2,
    city: row.city,
    state: row.state,
    zip: row.zip,
    phone: row.phone,
    fax: row.fax,
    email: row.email,
    website: row.website,
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

export class InvestorEncompassTemplateService {
  /**
   * Get all templates for an investor
   */
  async getTemplatesByInvestorId(investorId: string): Promise<InvestorEncompassTemplate[]> {
    log.info('Fetching templates for investor', { investorId });

    const { data: templates, error } = await supabase
      .from('investor_encompass_templates')
      .select('*')
      .eq('investor_id', investorId)
      .order('template_name');

    if (error) {
      log.error('Failed to fetch templates', { investorId }, error);
      throw error;
    }

    if (!templates || templates.length === 0) {
      return [];
    }

    // Fetch contacts for all templates
    const templateIds = templates.map((t) => t.id);
    const { data: contacts, error: contactsError } = await supabase
      .from('investor_encompass_template_contacts')
      .select('*')
      .in('template_id', templateIds);

    if (contactsError) {
      log.error('Failed to fetch contacts', { templateIds }, contactsError);
      // Return templates without contacts rather than failing completely
    }

    // Group contacts by template
    const contactsByTemplate: Record<string, InvestorEncompassTemplateContactRow[]> = {};
    if (contacts) {
      for (const contact of contacts) {
        if (!contactsByTemplate[contact.template_id]) {
          contactsByTemplate[contact.template_id] = [];
        }
        contactsByTemplate[contact.template_id].push(contact as InvestorEncompassTemplateContactRow);
      }
    }

    const assignments = await fetchProductAssignments(templateIds);

    const assignmentsByTemplate: Record<string, InvestorTemplateProductAssignmentRow[]> = {};
    for (const assignment of assignments) {
      if (!assignmentsByTemplate[assignment.template_id]) {
        assignmentsByTemplate[assignment.template_id] = [];
      }
      assignmentsByTemplate[assignment.template_id].push(assignment);
    }

    return templates.map((t) =>
      transformTemplate(t as InvestorEncompassTemplateRow, contactsByTemplate[t.id], assignmentsByTemplate[t.id])
    );
  }

  /**
   * Get a template by ID with contacts
   */
  async getTemplateById(templateId: string): Promise<InvestorEncompassTemplate | null> {
    log.info('Fetching template by ID', { templateId });

    const { data: template, error } = await supabase
      .from('investor_encompass_templates')
      .select('*')
      .eq('id', templateId)
      .maybeSingle();

    if (error) {
      log.error('Failed to fetch template', { templateId }, error);
      throw error;
    }

    if (!template) {
      return null;
    }

    // Fetch contacts
    const { data: contacts, error: contactsError } = await supabase
      .from('investor_encompass_template_contacts')
      .select('*')
      .eq('template_id', template.id);

    if (contactsError) {
      log.error('Failed to fetch contacts', { templateId }, contactsError);
    }

    const assignments = await fetchProductAssignments([template.id]);

    return transformTemplate(
      template as InvestorEncompassTemplateRow,
      (contacts || []) as InvestorEncompassTemplateContactRow[],
      assignments
    );
  }

  /**
   * Sync templates from Encompass via edge function
   * Uses raw fetch() for reliable auth header passing
   */
  async syncTemplates(request: SyncInvestorTemplatesRequest): Promise<SyncInvestorTemplatesResponse> {
    log.info('Syncing templates', request);

    const { data: session } = await supabase.auth.getSession();

    if (!session?.session?.access_token) {
      throw new Error('Not authenticated');
    }

    // Use raw fetch instead of supabase.functions.invoke for reliable auth
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-investor-templates`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          action: request.encompassTemplateId || request.templateProductScopes?.length ? 'sync' : 'syncAll',
          investorId: request.investorId,
          encompassTemplateId: request.encompassTemplateId,
          templateProductScopes: request.templateProductScopes,
          forceRefresh: request.forceRefresh
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      log.error('Sync failed', request, new Error(errorData.error || `HTTP ${response.status}`));
      throw new Error(errorData.error || 'Sync failed');
    }

    const result = (await response.json()) as SyncInvestorTemplatesResponse;
    log.info('Sync completed', { synced: result.synced, errors: result.errors?.length || 0 });

    return result;
  }

  /**
   * List available templates from Encompass (for linking UI)
   * Uses raw fetch() for reliable auth header passing
   */
  async listAvailableTemplates(): Promise<
    Array<{
      id: string;
      templateName: string;
      path?: string;
      investorName?: string;
      investorCode?: string;
    }>
  > {
    log.info('Listing available templates from Encompass');

    const { data: session } = await supabase.auth.getSession();

    if (!session?.session?.access_token) {
      throw new Error('Not authenticated');
    }

    // Use raw fetch instead of supabase.functions.invoke for reliable auth
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-investor-templates`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ action: 'list' })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      log.error('List templates failed', {}, new Error(errorData.error || `HTTP ${response.status}`));
      throw new Error(errorData.error || 'Failed to list templates');
    }

    const data = await response.json();
    return data.templates || [];
  }

  /**
   * Get all linked templates with their investor names
   * Used to disable already-linked templates in the linking dialog
   */
  async getLinkedTemplates(): Promise<
    Map<string, { investorId: string; investorName: string }>
  > {
    log.info('Fetching linked templates');

    const { data, error } = await supabase
      .from('investor_encompass_templates')
      .select('encompass_template_id, investor_id, investors!inner(investor_name)')
      .order('encompass_template_id');

    if (error) {
      log.error('Failed to fetch linked templates', {}, error);
      throw error;
    }

    const map = new Map<string, { investorId: string; investorName: string }>();
    for (const row of data || []) {
      // Handle the nested investor object from the join
      const investorData = row.investors as unknown as { investor_name: string } | null;
      map.set(row.encompass_template_id, {
        investorId: row.investor_id,
        investorName: investorData?.investor_name || 'Unknown'
      });
    }

    log.info('Fetched linked templates', { count: map.size });
    return map;
  }

  /**
   * Link an Encompass template to an investor.
   *
   * Product-scoped templates are allowed. The authoritative edge/RPC path
   * still prevents an Encompass template id from being linked to a different
   * investor in the same organization.
   */
  async linkTemplate(
    investorId: string,
    encompassTemplateId: string
  ): Promise<void> {
    log.info('Linking template', { investorId, encompassTemplateId });

    // Sync the template (which will create/update the link)
    await this.syncTemplates({
      investorId,
      encompassTemplateId
    });

    log.info('Template linked successfully');
  }

  async linkTemplatesWithProductScopes(
    investorId: string,
    templateProductScopes: SyncInvestorTemplatesRequest['templateProductScopes']
  ): Promise<SyncInvestorTemplatesResponse> {
    log.info('Linking product-scoped templates', {
      investorId,
      count: templateProductScopes?.length ?? 0
    });

    if (!templateProductScopes || templateProductScopes.length === 0) {
      throw new Error('At least one template product scope is required');
    }

    const result = await this.syncTemplates({
      investorId,
      templateProductScopes
    });

    if (!result.success) {
      throw new Error(result.errors?.[0]?.error || result.message || 'Failed to link product-scoped templates');
    }

    return result;
  }

  /**
   * Unlink a template from an investor
   */
  async unlinkTemplate(templateId: string): Promise<void> {
    log.info('Unlinking template', { templateId });

    const { error } = await supabase.rpc('unlink_investor_template', {
      p_template_id: templateId
    });

    if (error) {
      log.error('Failed to delete template', { templateId }, error);
      throw Object.assign(new Error(error.message), error);
    }

    log.info('Template unlinked successfully');
  }

  async assignTemplateProducts(
    investorId: string,
    assignments: Array<{ templateId: string; product: string }>,
    reviewId?: string | null
  ): Promise<void> {
    const { error } = await supabase.rpc('assign_investor_template_products', {
      p_investor_id: investorId,
      p_assignments: assignments,
      p_review_id: reviewId ?? null
    });

    if (error) {
      log.error('Failed to assign template products', { investorId }, error);
      throw Object.assign(new Error(error.message), error);
    }
  }

  /**
   * Get contacts grouped by category for display
   */
  getContactsByGroup(
    contacts: EncompassTemplateContact[]
  ): Record<string, EncompassTemplateContact[]> {
    const groups: Record<string, EncompassTemplateContact[]> = {
      delivery: [],
      servicing: [],
      insurance: [],
      other: []
    };

    const groupMapping: Record<string, string> = {
      NoteDelivery: 'delivery',
      LoanDelivery: 'delivery',
      ShipTo: 'delivery',
      TrailingDocs: 'delivery',
      CustomerService: 'servicing',
      Payment: 'servicing',
      TaxNotice: 'servicing',
      MortgageInsurance: 'insurance',
      Insurance: 'insurance',
      Assignment: 'other',
      Correspondence: 'other',
      Secondary: 'other'
    };

    for (const contact of contacts) {
      const group = groupMapping[contact.contactType];
      if (!group) {
        continue;
      }
      groups[group].push(contact);
    }

    return groups;
  }

  /**
   * Check if a contact has any meaningful data
   */
  hasContactData(contact: EncompassTemplateContact | null): boolean {
    if (!contact) {
return false;
}
    return !!(
      contact.investorName ||
      contact.contactName ||
      contact.addressStreet1 ||
      contact.phone ||
      contact.email ||
      contact.website
    );
  }
}

// Export singleton instance
export const investorEncompassTemplateService = new InvestorEncompassTemplateService();
